import { useMemo, useState } from "react";
import { DataTable as PrTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import { InputText } from "primereact/inputtext";
import "./DataTable.scss";

/**
 * Properties Object definition used in the DataTable component.
 * @typedef {Object} DataTableProps
 * @property {Array.<Object>} columns
 * @property {Array.<Object>} rows
 * @property {string} columnIdName
 * @property {Function} onRowClick
 * @property {number} [rowsPerPage=10] - Number of rows per page (optional, default is 10)
 * @property {string[]} [globalFilterFields] - If provided and non-empty, enables a single text filter across these fields.
 * @property {string} [filterPlaceholder="Search"] - Placeholder for the search box.
 */

/**
 * @component
 * @param {DataTableProps} props
 */
const DataTable = (props) => {
  const {
    rows,
    rowsPerPage = 10,
    rowClassName,
    onRowClick,
    columns,
    globalFilterFields,
    filterPlaceholder = "Search"
  } = props;

  const [filterText, setFilterText] = useState("");

  const filteringEnabled = Array.isArray(globalFilterFields) && globalFilterFields.length > 0;

  const filters = useMemo(() => {
    if (!filteringEnabled) {
      return undefined;
    }

    return {
      global: {
        value: filterText,
        matchMode: FilterMatchMode.CONTAINS
      }
    };
  }, [filteringEnabled, filterText]);

  if ((rows) && (rows.length)) {
    const rowsPerPageOptions = [rowsPerPage, 25, 100].sort((a, b) => a - b);

    const tableProps = {
      value: rows,
      paginator: rows.length > rowsPerPage,
      removableSort: true,
      rows: rowsPerPage,
      rowsPerPageOptions: rowsPerPageOptions,
      paginatorTemplate: "CurrentPageReport RowsPerPageDropdown FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink",
      currentPageReportTemplate: "{first} - {last} of {totalRecords}",
      rowClassName: rowClassName
    };

    if (onRowClick) {
      tableProps.onRowClick = onRowClick;
      tableProps.selectionMode = "single";
    }

    if (filteringEnabled) {
      tableProps.filters = filters;
      tableProps.globalFilterFields = globalFilterFields;
      tableProps.filterDisplay = "menu";
      tableProps.header = (
        <div className="dt-header">
          <div className="dt-searchbar" aria-label="Table search">
            <InputText
              type="search"
              inputMode="search"
              value={filterText}
              onChange={(e) => {
                setFilterText(e.target.value);
              }}
              placeholder={filterPlaceholder}
              className="dt-search-input p-inputtext-sm"
              aria-label="Filter table rows"
              autoComplete="off"
            />
            <span className="dt-icon" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M21 21l-4.35-4.35m1.35-5.65a7 7 0 11-14 0 7 7 0 0114 0z"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          </div>
        </div>
      );
    }

    return (
      <PrTable {...tableProps}>
        {columns.map((col, i) => (
          <Column
            key={col.field || i}
            field={col.field}
            sortable={col.sortable !== undefined ? col.sortable : true}
            header={col.headerName}
            body={col.valueGetter}
            headerClassName={col.class}
            style={col.style}
          />
        ))}
      </PrTable>
    );
  }

  return null;
};

export default DataTable;